<template>
    <hox-notification-item
        :progress-percentage="progressPercentage"
        :show-clear-button="hasFailed || hasCompleted"
        :show-error-message="hasFailed"
        :show-abort-button="showAbortButton"
        :status="notification.status"
        :notification="notification"
        :is-job-finished="isJobFinished"
        @clear="removeNotification"
        @notificationDeleted="deleteCompletedNotification"
    >
        <template #title>
            <span v-if="notification.job.campaignDetails && notification.job.clientDetails">
                Client:
                <strong>{{ notification.job.clientDetails?.name }}</strong>
                <br />
                Campaign:
                <strong>{{ notification.job.campaignDetails?.name }}</strong>
            </span>
            {{ title }}
        </template>
        <template #subtitle>
            {{ statusText }}
        </template>
        <template #errorMessage>
            <p>{{ errorMessage }}</p>
            <p>Please try again in a few moments.</p>
        </template>
    </hox-notification-item>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { PLATFORM } from "hoxton-shared/enums/exports";

import { type RootState } from "@/store/types";
import { NotificationsAction, type Notification } from "@/store/modules/notifications";

import { NotificationStatus } from "@/enums/notifications";
import { PlatformLabel } from "@/enums/platforms";

type Props = {
    notification: Notification;
    showAbortButton: boolean;
    isJobFinished?: boolean;
};

const { notification, showAbortButton, isJobFinished } = defineProps<Props>();

const emits = defineEmits(["notificationDeleted"]);
const store = useStore<RootState>();

const numberOfAds = computed(() => {
    return notification.deliverablesCount || notification.job?.payload?.publishingAdsCount || "Many";
});

const adOrAds = computed(() => {
    return numberOfAds.value === 1 ? "ad" : "ads";
});

const hasCompleted = computed(() => {
    return notification.status === NotificationStatus.Completed;
});

const hasFailed = computed(() => {
    return notification.status === NotificationStatus.Failed;
});

const completedStatusText = computed(() => {
    const platformLabel = notification.platform
        ? PlatformLabel[notification.platform]
        : PlatformLabel[PLATFORM.CREATIVEX];

    const prefix =
        notification.platform === PLATFORM.SATALIA
            ? `${numberOfAds.value} ${adOrAds.value} sent`
            : `${numberOfAds.value} ${adOrAds.value} sent to ${platformLabel}`;
    return `${prefix} for pre-flight analysis`;
});

const statusText = computed(() => {
    if (notification.status === NotificationStatus.InProgress) {
        const platformLabel = notification.platform
            ? PlatformLabel[notification.platform]
            : PlatformLabel[PLATFORM.CREATIVEX];

        const prefix =
            notification.platform === PLATFORM.SATALIA
                ? `Sending ${numberOfAds.value} ${adOrAds.value}`
                : `Sending ${numberOfAds.value} ${adOrAds.value} to ${platformLabel}`;
        return `${prefix} for pre-flight analysis`;
    }
    if (hasCompleted.value) {
        return completedStatusText.value;
    }
    if (hasFailed.value) {
        return "Failed";
    }
    return undefined;
});

const title = computed(() => {
    const platformLabel = notification.platform
        ? PlatformLabel[notification.platform]
        : PlatformLabel[PLATFORM.CREATIVEX];

    if (platformLabel === "Pre-flight") {
        return "Pre-flight";
    }

    return `Pre-flight - ${platformLabel}`;
});

const progressPercentage = computed(() => {
    if (hasCompleted.value) {
        return 100;
    }
    return notification.job?.progress;
});

const errorMessage = computed(() => {
    return notification.job?.message === "Error publishing the feed"
        ? "Error while sending for pre-flight analysis"
        : notification.job?.message || "An error occurred.";
});

const removeNotification = () => {
    store.dispatch(NotificationsAction.RemoveItem, notification.id);
};

const deleteCompletedNotification = () => {
    emits("notificationDeleted");
};
</script>

<style lang="scss">
@use "@style/variables" as *;

.publish-notification-item__list-item {
    list-style-position: inside;
    margin-left: 0 !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
